import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { Sucursal, getSucursales } from "@/services/accounts.service";
import { ProductionZoneDetailResponse } from "@/services/production-zones.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { Flex } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { ProductionZoneForm } from "../components/ProductionZoneForm";
import { hasEmptyProductionZoneFormValues } from "../utils/production-zone-form";
import {
  ProductionZoneCreationForm,
  productionZoneCreationSchema,
} from "../create/schema/production-zone-creation.schema";
import {
  fetchProductionZoneHandler,
  updateProductionZoneHandler,
} from "./schema/handler-functions";

export const ProductionZoneDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const [isActive, setIsActive] = useState(false);
  const [loadingProductionZone, setLoadingProductionZone] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [loadingSucursales, setLoadingSucursales] = useState(false);
  const [productionZone, setProductionZone] =
    useState<ProductionZoneDetailResponse | null>(null);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const fetchSucursalesList = async () => {
    setLoadingSucursales(true);
    const response = await getSucursales();
    setSucursales((response ?? []) as Sucursal[]);
    setLoadingSucursales(false);
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProductionZoneCreationForm>({
    resolver: zodResolver(productionZoneCreationSchema),
    defaultValues: {
      nombre: "",
      zonaComercial: [],
    },
  });

  useEffect(() => {
    if (isActive && id) {
      fetchSucursalesList();
      fetchProductionZoneHandler({
        id: Number(id),
        setLoadingProductionZone,
        setProductionZone,
        navigate,
        fetchPermissions,
      });
    }
  }, [isActive, id]);

  useEffect(() => {
    if (!productionZone) return;


    reset({
      nombre: productionZone.nombre,
      zonaComercial: [
        String(productionZone.zonaComercial),
      ],
      activo: [productionZone.activo],
    });
  }, [productionZone, reset]);

  const watchedValues = useWatch({ control });

  const hasNoFilledValues = useMemo(() => {
    return hasEmptyProductionZoneFormValues(watchedValues, true);
  }, [watchedValues]);

  const handleCancel = () => {
    navigate(AppRoutes.PRODUCTION_ZONES);
  };

  const onSubmit = async (values: ProductionZoneCreationForm) => {
    if (!id) return;

    await updateProductionZoneHandler({
      id: Number(id),
      values,
      setLoadingUpdate,
      navigate,
      fetchPermissions,
    });
  };

  const loading = loadingProductionZone || loadingSucursales || loadingUpdate;

  return (
    <Flex
      as="form"
      onSubmit={handleSubmit(onSubmit)}
      flexDir="column"
      paddingTop="20px"
      gap="12px"
    >
      <SectionHeader
        title="Editar Zona Productiva"
        addButtonLabel="Guardar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        addButtonType="submit"
        disableAdd={hasNoFilledValues || loading}
        loading={loading}
      />

      <ProductionZoneForm
        control={control}
        errors={errors}
        sucursales={sucursales}
        isActive={isActive}
        loading={loading}
        loadingSucursales={loadingSucursales}
        showStatusField
      />
    </Flex>
  );
};
